import React, { useCallback, useRef } from 'react';
import { Button, Input, Modal, Space, TableProps, message, Switch } from 'antd';
import type { SorterResult, FilterValue } from 'antd/es/table/interface';
import CommonTable from 'components/CommonTable';
import type { ColumnsType } from 'antd/es/table';
import s from './s.module.less';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import dayjs from 'dayjs';
import type { TAssessmentItem, IAssessmentInput } from 'types/operation';
import AssessmentUpdateModal from '../AssessmentUpdateModal';
import ExternalLinkIcon from 'assets/common/externalLinkIcon.svg';
import {
    getAssessmentList,
    changeAssessmentStatus,
    addOrUpdateAssessment,
} from 'api/operation/assessment';
import { EnumFields } from 'types/enumerationData';
import useSettings from 'hooks/useSettings';
import { Meta, EIntakeFormType } from 'types/common';
import { EPageStatus, EKlarityPageStatus } from 'types/helloKlarity';
import { getHelloKlarityHost } from 'utils/common';
import { EFeatureType } from 'constants/textVisit';
import debounce from 'lodash/debounce';

const Index = () => {
    const navigation = useNavigate();
    const [setting] = useSettings();

    // Workflows state
    const [itemList, setItemList] = React.useState<TAssessmentItem[]>([]);
    const [total, setTotal] = React.useState<number>(0);
    const [loading, setLoading] = React.useState(false);
    const [, contextHolder] = Modal.useModal();
    const [openAssessmentModal, setOpenAssessmentModal] = React.useState(false);
    const [isOpenErrorModal, setIsOpenErrorModal] = React.useState(false);
    const [errorContent, setErrorContent] = React.useState<string[]>([]);
    const [itemStatus, setItemStatus] = React.useState<EPageStatus>(EPageStatus.ADD);
    const [assessmentItem, setAssessmentItem] =
        React.useState<TAssessmentItem>();
    const [searchOrderType, setOrderType] = React.useState<string>('DESC');
    const [searchOrderKey, setOrderKey] = React.useState<string>('UPDATE_AT');
    const pageSize = 10;

    const [page, setPage] = React.useState(1);
    const [pendingProviderNameFilter, setPendingProviderNameFilter] =
        React.useState('');
    const [nameFilter, setNameFilter] = React.useState<string>('');
    const [idFilter] = React.useState<string>('');
    const [tagsFilter] = React.useState<string>('');
    const [statusFilter, setStatus] = React.useState<string>(
        '',
    );
    // Note: Intake workflows no longer uses "Condition" (display/filter/submission removed)

    const doFetch = useCallback(async ({
        current,
        id,
        assessmentName,
        orderKey,
        orderType,
        tags,
        status,
    }: IAssessmentInput) => {
        setLoading(true);
        const reqParam: IAssessmentInput = {
            current: current || page,
            size: pageSize,
            id: id || idFilter,
            assessmentName: assessmentName || nameFilter,
            orderKey: orderKey || searchOrderKey,
            orderType: orderType || searchOrderType,
            tags: tags || tagsFilter || [],
            status: status || statusFilter || '',
            featureType: EFeatureType.INTAKE_FORM_WORKFLOW,
        };
        const result = await getAssessmentList(reqParam);
        if (!result.error) {
            setItemList(result?.data?.data?.records);
            setTotal(result?.data?.data?.total);
        }
        setLoading(false);
    }, [page, idFilter, nameFilter, searchOrderKey, searchOrderType, tagsFilter, statusFilter]);

    const onTableChange: TableProps<TAssessmentItem>['onChange'] = (
        pagination,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<TAssessmentItem> | SorterResult<TAssessmentItem>[],
    ) => {
        const pageInx = pagination?.current || 1;
        setPage(pageInx);
        // condition filter removed
        if (filters.status && Array.isArray(filters.status) && filters.status.length === 1) {
            setStatus(filters.status[0] === 'ACTIVE' ? 'A' : 'D');
        } else {
            setStatus('');
        }
        if (sorter && !Array.isArray(sorter) && sorter.order) {
            const orderType =
                sorter.order === 'ascend' ? 'ASC' : 'DESC';
            const orderKey =
                sorter.field === 'updateAt' ? 'UPDATE_AT' : (sorter.field as string || 'UPDATE_AT');
            setOrderType(orderType);
            setOrderKey(orderKey);
        }
    };

    const addNewAssessment = () => {
        setOpenAssessmentModal(true);
        setItemStatus(EPageStatus.ADD);
        setAssessmentItem(undefined);
    };

    const handleStatusChange = async (record: TAssessmentItem) => {
        const { data } = await changeAssessmentStatus({
            id: record.id || '',
            status:
                record.status === EKlarityPageStatus.ACTIVE
                    ? EKlarityPageStatus.INACTIVATE
                    : EKlarityPageStatus.ACTIVE,
        });
        if (Array.isArray(data?.data) && data?.data.length === 0) {
            message.success('updated successfully');
            doFetch({});
        } else {
            setAssessmentItem(record);
            setIsOpenErrorModal(true);
            setErrorContent(data?.data || []);
        }
    };

    const handleEnableHelloKlarityDisplayChange = async (
        checked: boolean,
        record: TAssessmentItem,
    ) => {
        try {
            const result = await addOrUpdateAssessment({
                ...record,
                id: record.id,
                enableHelloKlarityDisplay: checked,
            });
            if (!result.error) {
                message.success('Updated successfully');
                doFetch({});
            } else {
                message.error('Update failed');
            }
        } catch (_error) {
            message.error('Update failed');
        }
    };

    const handleCancel = () => {
        setIsOpenErrorModal(false);
        setErrorContent([]);
    };

    const onSuccess = () => {
        setOpenAssessmentModal(false);
        setPage(1);
        doFetch({});
        message.success('Save successfully');
    };

    const handleDuplicate = (record: TAssessmentItem) => {
        setAssessmentItem(record);
        setItemStatus(EPageStatus.DUPLICATE);
        setOpenAssessmentModal(true);
    };

    const handlePreviewLink = (record: TAssessmentItem) => {
        const rec = record as TAssessmentItem & {
            specialtyType?: string | string[];
            specialty?: string | string[];
            specialties?: string | string[];
            appointmentType?: string | string[];
            serviceType?: string | string[];
            serviceTypes?: string | string[];
        };
        const specialtyRaw = rec?.specialtyType ?? rec?.specialty ?? rec?.specialties ?? '';
        const serviceTypeRaw = rec?.appointmentType ?? rec?.serviceType ?? rec?.serviceTypes ?? '';
        const specialty = Array.isArray(specialtyRaw)
            ? specialtyRaw[0]
            : String(specialtyRaw || '').split(',').filter(Boolean)[0] ?? '';
        const serviceType = Array.isArray(serviceTypeRaw)
            ? serviceTypeRaw[0]
            : String(serviceTypeRaw || '').split(',').filter(Boolean)[0] ?? '';
        const url = `${getHelloKlarityHost()}/consent-forms?specialty=${encodeURIComponent(specialty)}&serviceType=${encodeURIComponent(serviceType)}&type=preview&previewType=${EIntakeFormType.SPECIFIC_INTAKE_FORM}`;
        window.open(url, '_blank');
    };

    React.useEffect(() => {
        doFetch({});
    }, [page, idFilter, nameFilter, tagsFilter, searchOrderKey, searchOrderType, doFetch]);

    const columns: ColumnsType<TAssessmentItem> = [
        {
            title: 'WORKFLOW NAME',
            dataIndex: 'assessmentName',
            key: 'assessmentName',
            width: '220px',
            filterDropdown: ({ close }: { close: () => void }) => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search name"
                        value={pendingProviderNameFilter}
                        onChange={(e) => {
                            setPendingProviderNameFilter(e.target.value);
                        }}
                        onPressEnter={() => {
                            setNameFilter(pendingProviderNameFilter);
                            setPage(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setNameFilter(pendingProviderNameFilter);
                                setPage(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingProviderNameFilter('');
                                setNameFilter('');
                                setPage(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
            ),
        },
        {
            title: 'SPECIALTY',
            dataIndex: 'specialtyType',
            key: 'specialty',
            width: '220px',
            filters: (setting?.[EnumFields.HELLO_KLARITY_SPECIALTY] || []).map(
                (opt: Meta) => ({
                    text: opt.label,
                    value: opt.value,
                }),
            ),
            filterMultiple: true,
            onFilter: (value: string | number | boolean, record: TAssessmentItem) => {
                const rec = record as any;
                const raw = rec?.specialtyType ?? rec?.specialty ?? rec?.specialties ?? '';
                const arr = Array.isArray(raw) ? raw : String(raw || '').split(',').filter(Boolean);
                return arr.includes(value as string);
            },
            render: (_val: unknown, record: TAssessmentItem) => {
                const rec = record as any;
                const raw = rec?.specialtyType ?? rec?.specialty ?? rec?.specialties ?? '';
                const values = Array.isArray(raw) ? raw : String(raw || '').split(',').filter(Boolean);
                const options = (setting?.[EnumFields.HELLO_KLARITY_SPECIALTY] || []) as Meta[];
                const labels = values.map((v: string) => options.find((o) => o.value === v)?.label || v);
                return <div>{labels.length ? labels.join(', ') : '-'}</div>;
            },
        },
        {
            title: 'APPOINTMENT TYPE',
            dataIndex: 'appointmentType',
            key: 'appointmentType',
            width: '220px',
            filters: (setting?.[EnumFields.SERVICE_TYPE] || []).map(
                (opt: Meta) => ({
                    text: opt.label,
                    value: opt.value,
                }),
            ),
            filterMultiple: true,
            onFilter: (value: string | number | boolean, record: TAssessmentItem) => {
                const rec = record as any;
                const raw = rec?.appointmentType ?? rec?.serviceType ?? rec?.serviceTypes ?? '';
                const arr = Array.isArray(raw) ? raw : String(raw || '').split(',').filter(Boolean);
                return arr.includes(value as string);
            },
            render: (_val: unknown, record: TAssessmentItem) => {
                const rec = record as any;
                const raw = rec?.appointmentType ?? rec?.serviceType ?? rec?.serviceTypes ?? '';
                const values = Array.isArray(raw) ? raw : String(raw || '').split(',').filter(Boolean);
                const options = (setting?.[EnumFields.SERVICE_TYPE] || []) as Meta[];
                const labels = values.map((v: string) => options.find((o) => o.value === v)?.label || v);
                return <div>{labels.length ? labels.join(', ') : '-'}</div>;
            },
        },
        {
            title: 'STATUS',
            dataIndex: 'status',
            width: '160px',
            filters: [
                {
                    text: 'Active',
                    value: 'ACTIVE',
                },
                {
                    text: 'Inactive',
                    value: 'INACTIVE',
                },
            ],
            render: (val: string) => {
                return (
                    <div className={s.cell}>
                        {val === 'A' && 'Active'}
                        {val === 'D' && 'Inactive'}
                    </div>
                );
            },
        },
        {
            title: 'LAST MODIFIED',
            dataIndex: 'updateAt',
            sortDirections: ['ascend', 'descend', 'ascend'],
            sorter: true,
            width: '150px',
            className: s.tableColumn,
            render: (time: string) => {
                return <div>{time ? dayjs(time).format('MM/DD/YYYY') : 'Nil'}</div>;
            },
        },
        {
            title: 'QUESTION FROM',
            dataIndex: 'questionsFormLink',
            width: '150px',
            className: s.tableColumn,
            render: (val: string) => {
                return val ? (
                    <a href={val} target="_blank" className={s.link} rel="noreferrer">
                        Form link
                        <img src={ExternalLinkIcon} />
                    </a>
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'RESULTS',
            dataIndex: 'questionsFormResultLink',
            width: '150px',
            className: s.tableColumn,
            render: (val: string) => {
                return val ? (
                    <a href={val} target="_blank" className={s.link} rel="noreferrer">
                        Result link
                        <img src={ExternalLinkIcon} />
                    </a>
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'DISPLAY',
            dataIndex: 'enableHelloKlarityDisplay',
            width: '120px',
            className: s.tableColumn,
            render: (val: boolean, record: TAssessmentItem) => {
                return (
                    <Switch
                        checked={val || false}
                        onChange={(checked) =>
                            handleEnableHelloKlarityDisplayChange(checked, record)
                        }
                    />
                );
            },
        },
        {
            title: '',
            width: '200px',
            fixed: 'right',
            render: (p: TAssessmentItem) => {
                return (
                    <div className={s.actionWrap}>
                        <Button
                            className={s.link}
                            onClick={() => handleStatusChange(p)}
                            type="text"
                        >
                            {p.status === EKlarityPageStatus.ACTIVE
                                ? 'De-activate'
                                : 'Activate'}
                        </Button>
                        <span
                            onClick={() => {
                                handleDuplicate(p);
                            }}
                            className={s.link}
                        >
                            Duplicate
                        </span>
                        {p.status === EKlarityPageStatus.ACTIVE ? (
                            <span
                                onClick={() => handlePreviewLink(p)}
                                className={s.link}
                            >
                                Link
                                <img src={ExternalLinkIcon} alt="" />
                            </span>
                        ) : (
                            <span className={s.emptyLink}>-</span>
                        )}
                        <Button
                            className={s.link}
                            onClick={() => {
                                navigation(
                                    `${PATH.OPERATION}/${PATH.INTAKE_WORKFLOWS_DETAIL}?id=${p.id}&uuid=${p.uuid}`,
                                );
                            }}
                            type="text"
                        >
                            View
                        </Button>
                    </div>
                );
            },
        },
    ].filter(Boolean) as ColumnsType<TAssessmentItem>;

    // 防抖搜索函数
    const debouncedSearch = useRef(
        debounce((value: string) => {
            setNameFilter(value);
            setPage(1);
        }, 500)
    ).current;

    // 清理防抖函数
    React.useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {value} = e.target;
        setPendingProviderNameFilter(value);
        // 使用防抖搜索
        debouncedSearch(value);
    };

    const handleSearchPressEnter = () => {
        // 按 Enter 键立即搜索，取消防抖
        debouncedSearch.cancel();
        setNameFilter(pendingProviderNameFilter);
        setPage(1);
    };

    const handleSearchClear = () => {
        debouncedSearch.cancel();
        setPendingProviderNameFilter('');
        setNameFilter('');
        setPage(1);
    };

    return (
        <div className={s.wrap}>
            <div className={s.selectBar}>
                <Input
                    className={s.searchInput}
                    placeholder="Search workflows..."
                    prefix={<SearchOutlined />}
                    value={pendingProviderNameFilter}
                    onChange={handleSearchChange}
                    onPressEnter={handleSearchPressEnter}
                    onClear={handleSearchClear}
                    allowClear
                />
                <Button onClick={addNewAssessment} icon={<PlusOutlined />}>
                    Add a new workflow
                </Button>
            </div>
            <CommonTable
                bordered
                rowKey="id"
                pagination={{
                    total,
                    current: page,
                    pageSize,
                    showSizeChanger: false,
                }}
                scroll={{ x: 'max-content' }}
                loading={loading}
                columns={columns}
                onChange={onTableChange}
                data={itemList || []}
            />
            {openAssessmentModal && (
                <AssessmentUpdateModal
                    item={assessmentItem}
                    itemStatus={itemStatus}
                    onCancel={() => setOpenAssessmentModal(false)}
                    onSuccess={onSuccess}
                />
            )}
            <Modal
                title=""
                open={isOpenErrorModal}
                destroyOnClose
                okText="Save"
                onCancel={handleCancel}
                closeIcon={<span />}
                footer={
                    <div>
                        <Button onClick={handleCancel}>Cancel</Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                navigation(
                                    `${PATH.OPERATION}/${PATH.INTAKE_WORKFLOWS_DETAIL}?id=${assessmentItem?.id}&uuid=${assessmentItem?.uuid}`,
                                );
                            }}
                        >
                            Edit
                        </Button>
                    </div>
                }
            >
                <div className={s.popupContent}>
                    <div className={s.popupContentInner}>
                        <h3 className={s.popupTitle}>
                            This test cannot be activated due to missing
                            information
                        </h3>
                        <ul className={s.popupDetail}>
                            {errorContent.map((item: string, index: number) => {
                                return <li key={index}>{item}</li>;
                            })}
                        </ul>
                    </div>
                </div>
            </Modal>
            {contextHolder}
        </div>
    );
};

export default Index;


